import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Subject, takeUntil } from 'rxjs';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { CstIpiTexto, getCstIpiTexto } from '@shared/constants/motor-fiscal/cst-ipi-metadata';
import {
    IndicadoresCst,
    MapaCamposIndicador,
    aplicarObrigatoriedadeIndicadores,
    controlesDasRegrasEntreCampos,
    isAlgumCampoAtivo,
    isCampoAtivo,
    isCodigoDaOperacao,
    lerIndicadoresCst,
    limparCamposInativos,
} from '@shared/constants/motor-fiscal/indicadores-campos.util';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { MaskService } from '@shared/services/mask.service';
import { CstIpiStruct } from '@shared/services/structs/motor-fiscal/cst-ipi.struct';

/**
 * Campo do indicador do CST do IPI -> control do grupo `ipi` do item da venda. Os demais controls de uma chave são
 * limpos junto quando o campo fica inativo.
 */
const MAPA_CAMPOS_IPI_VENDA: MapaCamposIndicador = {
    pIPI: ['percentualAliquota', 'valorBaseCalculo', 'valorIpi'],
    vUnid: ['valorUnidade', 'quantidadeUnidade'],
};

/** Aba "IPI" do modal do item da venda (cópia da aba IPI do item da NF-e de saída), dinâmica por CST. */
@Component({
    selector: 'app-vd-item-aba-ipi',
    standalone: true,
    imports: [
        AbntPipe,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        NgxCurrencyDirective,
        AlertCardComponent,
        EmptyStateComponent,
    ],
    templateUrl: './vd-item-aba-ipi.component.html',
    styleUrls: ['../abas-comum.css', './vd-item-aba-ipi.component.css'],
})
export class VdItemAbaIpiComponent implements OnInit, OnChanges, OnDestroy {
    /** O grupo `ipi` do item. */
    @Input({ required: true }) public form!: FormGroup;
    @Input() public listaCstIpi: CstIpiStruct[] = [];
    @Input() public isRecalculoPendente: boolean = false;

    public readonly percentMask = this.maskService.getPercentMask();
    public readonly currencyMask = this.maskService.getDynamicCurrencyMask();
    public readonly valorMask = this.maskService.getCurrencyMask(2);
    public readonly decimalMask = this.maskService.getDynamicDecimalMask();

    private readonly destroy$ = new Subject<void>();

    constructor(private maskService: MaskService) {}

    public ngOnInit(): void {
        ['codigoCst', ...controlesDasRegrasEntreCampos(MAPA_CAMPOS_IPI_VENDA)].forEach((campo) =>
            this.form.get(campo)?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.sincronizarObrigatoriedade()));
        this.sincronizarObrigatoriedade();
    }

    /** Os indicadores chegam com a lista de CST (lookup assíncrono). */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['listaCstIpi'] && !changes['listaCstIpi'].firstChange) this.sincronizarObrigatoriedade();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** A venda só usa os CSTs de saída, pelo indicador. */
    public get listaCstIpiSaida(): CstIpiStruct[] {
        return this.listaCstIpi.filter((c) => isCodigoDaOperacao(c.indicadores, TipoOperacaoEnum.Saida));
    }

    public get indicadores(): IndicadoresCst {
        const cst = this.form?.get('codigoCst')?.value;

        return lerIndicadoresCst(this.listaCstIpi.find((c) => c.codigo === cst)?.indicadores);
    }

    public get cstIpiTexto(): CstIpiTexto | null {
        const cst = this.form?.get('codigoCst')?.value;

        return getCstIpiTexto(cst, this.listaCstIpi.find((c) => c.codigo === cst)?.descricao, this.indicadores);
    }

    public ativo(chave: string): boolean {
        return isCampoAtivo(this.indicadores, chave);
    }

    public algumAtivo(chaves: string[]): boolean {
        return isAlgumCampoAtivo(this.indicadores, chaves);
    }

    /** Troca feita pelo usuário: os campos que o novo CST não exibe (IPINT no leiaute, só CST) deixam de existir. */
    public onCstSelecionado(): void {
        limparCamposInativos(this.form, this.indicadores, MAPA_CAMPOS_IPI_VENDA);
    }

    /** Mesma regra da natureza: pelo indicador do CST, com a escolha entre a alíquota e o valor por unidade. */
    private sincronizarObrigatoriedade(): void {
        const isComCst = !!this.form.get('codigoCst')?.value;

        aplicarObrigatoriedadeIndicadores(this.form, isComCst ? this.indicadores : null, MAPA_CAMPOS_IPI_VENDA);
    }
}
